import React, { useState } from 'react'
import { useAppSelector } from '../../app/hooks'
import type { Dayjs } from 'dayjs'

import { produce } from 'immer'
import sensors from '../../utils/sensors'
import CONFIG from '../../constants/config'
import DatesPicker from '../components/DatesPickerV2'
import DepartmentMemberPicker from '../components/DepartmentMemberPicker'
import { CaretDownOutlined } from '@ant-design/icons'

// 部门类型（匹配 DepartmentMemberPicker 的 SelectedDepartment）
interface Department {
  id: string
  name: string
  num: number
}

// 筛选器数据类型
export interface AttendanceFilter {
  dates: [Dayjs, Dayjs]
  members: string[]
  userDepartmentIDs: string[]
  departments: Department[]
}

interface FilterProps {
  filter: AttendanceFilter
  onChange: (filter: AttendanceFilter) => void
}

// 成员选择器回调参数类型
interface MemberPickerParams {
  selectedNormalUserIDs: string[]
  selectedDepartmentUserIDs: string[]
  selectedDepartments: Department[]
}

function Filter(props: FilterProps) {
  const { filter, onChange } = props
  const members = useAppSelector((state) => state.group.members)
  const onlyInGroup = useAppSelector((state) => state.group.onlyInGroup)

  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [memberPickerVisible, setMemberPickerVisible] = useState(false)

  const handleDatesChange = (startDate: Dayjs, endDate: Dayjs) => {
    setShowDatePickerPane(false)
    const newFilter = produce(filter, (draftState) => {
      draftState.dates = [startDate, endDate]
    })
    onChange(newFilter)
    sensors.track('web_workgroup_attandence_page_click', {
      clickItem: 'filterTime',
      pagePath: window.location.pathname
    })
  }

  const handleMemberChangeV2 = (params: MemberPickerParams) => {
    setMemberPickerVisible(false)
    const {
      selectedNormalUserIDs,
      selectedDepartmentUserIDs,
      selectedDepartments
    } = params
    const newFilter = produce(filter, (draftState) => {
      draftState.members = selectedNormalUserIDs
      draftState.userDepartmentIDs = selectedDepartmentUserIDs
      draftState.departments = selectedDepartments
    })
    onChange(newFilter)
    sensors.track('web_workgroup_attandence_page_click', {
      clickItem: 'filterMember',
      pagePath: window.location.pathname
    })
  }

  const handleDepartmentUserPickerCancel = () => {
    setMemberPickerVisible(false)
    // 之前已选择已退出成员，开启【仅在团队】,然后取消筛选，需要过滤掉已退出成员重新请求数据
    if (onlyInGroup) {
      const allUserIDs = members.map((member) => member.user_id)
      const newFilter = produce(filter, (draftState) => {
        draftState.members = draftState.members.filter((userID) =>
          allUserIDs.includes(userID)
        )
        draftState.userDepartmentIDs = draftState.userDepartmentIDs.filter(
          (userID) => allUserIDs.includes(userID)
        )
      })
      onChange(newFilter)
    }
  }

  const handleFilterModals = (type: 'time' | 'member') => {
    if (type === 'time') {
      sensors.track('web_workgroup_filter_time_click', {
        pagePath: window.location.pathname
      })
      setShowDatePickerPane(true)
    }
    if (type === 'member') {
      setMemberPickerVisible(true)
    }
  }

  const handleCalcMemberPickedValue = () => {
    const userNumInDepartments = filter.departments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total =
      filter.members.length +
      filter.userDepartmentIDs.length +
      userNumInDepartments
    const hasSelection =
      filter.members.length > 0 ||
      filter.userDepartmentIDs.length > 0 ||
      filter.departments.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === members.length ||
      filter.departments.some((department) => department.id === '0')
    ) {
      return `全部成员（${members.length}人）`
    } else {
      return `已选${total}人`
    }
  }

  const { dates } = filter
  const isDatesOutOfMaxRange =
    Math.abs(dates[0].diff(dates[1], 'days', true)) > 31
  const isStartTimeAfterEndTime = dates[0].isAfter(dates[1])

  return (
    <div className="Filter">
      <div className="filter-wrapper">
        <div className="filter-item" onClick={() => handleFilterModals('time')}>
          <span className="filter-name">
            筛选时间
            <CaretDownOutlined />
          </span>
          <p>{dates[0].format(CONFIG.dateFormat)}</p>
          <p>{dates[1].format(CONFIG.dateFormat)}</p>
        </div>
        <div
          className="filter-item"
          onClick={() => handleFilterModals('member')}
        >
          <span className="filter-name">
            筛选成员
            <CaretDownOutlined />
          </span>
          <p>{handleCalcMemberPickedValue()}</p>
        </div>
        {isStartTimeAfterEndTime && (
          <p className="error-helper">结束时间应晚于开始时间</p>
        )}
        {!isStartTimeAfterEndTime && isDatesOutOfMaxRange && (
          <p className="error-helper">筛选时间范围不能超过31天</p>
        )}
      </div>
      <DatesPicker
        visible={showDatePickerPane}
        dates={dates}
        maxMonths={1}
        allowTime={false}
        onChange={handleDatesChange}
        onCancel={() => setShowDatePickerPane(false)}
      />
      <DepartmentMemberPicker
        open={memberPickerVisible}
        filterObj={filter}
        onOk={handleMemberChangeV2}
        onCancel={handleDepartmentUserPickerCancel}
      />
    </div>
  )
}

export default Filter
